import React, { useState, useRef, useCallback } from 'react';

const PATHS = {
  scarlett: "M32,4 C50,6 64,22 64,44 C64,66 52,88 40,104 C36,112 34,119 32,124 C30,119 28,112 24,104 C12,88 0,66 0,44 C0,22 14,6 32,4 Z",
  longtail: "M32,4 C40,6 44,18 44,36 C44,70 38,98 32,124 C26,98 20,70 20,36 C20,18 24,6 32,4 Z",
  camelot:  "M32,4 C48,7 60,22 60,42 C60,62 48,84 36,102 C34,110 33,118 32,122 C31,118 30,110 28,102 C16,84 4,62 4,42 C4,22 16,7 32,4 Z",
  rainbow:  "M32,4 C48,7 58,20 58,40 C58,62 46,86 34,106 C33,112 32,120 32,124 C32,120 31,112 30,106 C18,86 6,62 6,40 C6,20 16,7 32,4 Z",
  shamrock: "M32,4 C50,5 63,18 63,38 C64,58 52,82 40,100 C38,110 35,117 32,122 C29,117 26,110 24,100 C12,82 0,58 1,38 C2,18 14,5 32,4 Z",
  topknot:  "M32,10 C32,10 24,8 20,4 C18,2 18,0 22,1 C26,3 30,7 32,10 C34,7 38,3 42,1 C46,0 46,2 44,4 C40,8 32,10 32,10 C50,12 62,24 62,46 C62,68 50,90 38,108 C35,114 33,119 32,122 C31,119 29,114 26,108 C14,90 2,68 2,46 C2,24 14,12 32,10 Z",
};

const BARBS = {
  scarlett: [
    [32,24,12,36],[32,38,8,52],[32,52,5,67],[32,66,9,80],[32,80,15,92],
    [32,24,52,36],[32,38,56,52],[32,52,59,67],[32,66,55,80],[32,80,49,92],
  ],
  longtail: [
    [32,22,20,30],[32,34,18,43],[32,46,17,56],[32,58,18,67],[32,70,19,79],[32,82,20,90],
    [32,22,44,30],[32,34,46,43],[32,46,47,56],[32,58,46,67],[32,70,45,79],[32,82,44,90],
  ],
  camelot: [
    [32,22,13,34],[32,35,9,49],[32,48,7,63],[32,61,10,74],[32,73,16,84],
    [32,22,51,34],[32,35,55,49],[32,48,57,63],[32,61,54,74],[32,73,48,84],
  ],
  rainbow: [
    [32,22,14,33],[32,33,10,46],[32,44,8,59],[32,55,10,70],[32,66,14,79],
    [32,22,50,33],[32,33,54,46],[32,44,56,59],[32,55,54,70],[32,66,50,79],
  ],
  shamrock: [
    [32,22,11,33],[32,35,7,49],[32,48,5,63],[32,60,8,73],[32,72,14,83],
    [32,22,53,33],[32,35,57,49],[32,48,59,63],[32,60,56,73],[32,72,50,83],
  ],
  topknot: [
    [32,30,13,41],[32,43,9,56],[32,56,8,70],[32,68,11,80],[32,80,15,90],
    [32,30,51,41],[32,43,55,56],[32,56,56,70],[32,68,53,80],[32,80,49,90],
  ],
};

// Rainbow stripe colors - rich jewel tones
const RAINBOW_BANDS = ['#c62828','#e65100','#f9a825','#2e7d32','#1565c0','#6a1b9a'];

// Unique ID generator for SVG defs
let featherIdCounter = 0;

function FeatherSVG({ type, color, featherId }) {
  const bodyPath  = PATHS[type]  || PATHS.rainbow;
  const barbs     = BARBS[type]  || BARBS.rainbow;
  const isRainbow = type === 'rainbow';
  
  // Use featherId for unique SVG gradient IDs to prevent DOM conflicts
  const uniqueId = featherId || `f-${++featherIdCounter}`;
  const clipId = `clip-${uniqueId}`;

  // For rainbow, we render color bands; for others, solid fill
  // We'll use separate return statements to avoid conditional wrapper injection
  if (isRainbow) {
    return (
      <svg viewBox="0 0 64 128" xmlns="http://www.w3.org/2000/svg"
           style={{ width: '100%', height: '100%' }}>
        <defs>
          <clipPath id={clipId}>
            <path d={bodyPath}/>
          </clipPath>
        </defs>
        <path d={bodyPath} fill="rgba(0,0,0,0.5)" transform="translate(3,5)"/>
        <g clipPath={`url(#${clipId})`}>
          <rect x="0" y="0" width="64" height="24" fill="#c62828"/>
          <rect x="0" y="22" width="64" height="24" fill="#e65100"/>
          <rect x="0" y="44" width="64" height="24" fill="#f9a825"/>
          <rect x="0" y="66" width="64" height="24" fill="#2e7d32"/>
          <rect x="0" y="88" width="64" height="24" fill="#1565c0"/>
          <rect x="0" y="110" width="64" height="24" fill="#6a1b9a"/>
        </g>
        <g stroke="rgba(0,0,0,0.15)" strokeWidth="0.8">
          {barbs.map(([x1, y1, x2, y2], i) => (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}/>
          ))}
        </g>
        <line x1="32" y1="8" x2="32" y2="114"
              stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
        <path d={bodyPath} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
      </svg>
    );
  }

  // Non-rainbow feather - solid color fill
  return (
    <svg viewBox="0 0 64 128" xmlns="http://www.w3.org/2000/svg"
         style={{ width: '100%', height: '100%' }}>
      <path d={bodyPath} fill="rgba(0,0,0,0.5)" transform="translate(3,5)"/>
      <path d={bodyPath} fill={color}/>
      <g stroke="rgba(0,0,0,0.15)" strokeWidth="0.8">
        {barbs.map(([x1, y1, x2, y2], i) => (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}/>
        ))}
      </g>
      <line x1="32" y1="8" x2="32" y2="114"
            stroke="rgba(255,255,255,0.5)" strokeWidth="2"/>
      <path d={bodyPath} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
    </svg>
  );
}

export default function FeatherItem({
  feather,
  isPlaced,
  canPlace,
  onPlace,
  onWhisperStart,
  onStartAudio,
  whisperActive,
}) {
  const [isHeld, setIsHeld] = useState(false);
  const holdStartRef  = useRef(null);
  const holdTimerRef  = useRef(null);

  const startHold = useCallback((e) => {
    e.preventDefault();
    if (isPlaced) return;

    onStartAudio();
    holdStartRef.current = Date.now();
    setIsHeld(true);

    if (feather.whisperDelay && !whisperActive) {
      holdTimerRef.current = setTimeout(() => {
        if (feather.whisper) onWhisperStart(feather.whisper, feather.whisperAudio);
      }, feather.whisperDelay);
    }
  }, [isPlaced, feather, onWhisperStart, onStartAudio, whisperActive]);

  const endHold = useCallback(() => {
    if (holdStartRef.current === null) return;
    const duration = Date.now() - holdStartRef.current;

    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setIsHeld(false);
    holdStartRef.current = null;

    // Quick press = place
    if (duration < 280 && !isPlaced && canPlace) {
      onPlace(feather.id);
    }
  }, [isPlaced, canPlace, feather, onPlace]);

  const cancelHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    setIsHeld(false);
    holdStartRef.current = null;
  }, []);

  return (
    <div
      data-testid={`feather-${feather.id}`}
      style={{
        flex: '1',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '14px 10px 12px',
        background: isPlaced ? 'rgba(6,3,4,0.45)' : 'rgba(7,3,3,0.9)',
        border: isPlaced
          ? '1px solid rgba(61,28,9,0.18)'
          : '1px solid rgba(61,28,9,0.48)',
        borderRadius: '2px',
        boxShadow: isHeld
          ? undefined
          : isPlaced
            ? 'inset 0 2px 6px rgba(0,0,0,0.4)'
            : 'inset 0 2px 10px rgba(0,0,0,0.65)',
        cursor: isPlaced ? 'default' : 'pointer',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
        opacity: isPlaced ? 0.22 : 1,
        animation: isHeld ? 'breatheGlow 4s ease-in-out infinite' : 'none',
        transition: 'opacity 0.4s ease, border-color 0.3s ease',
      }}
      onPointerDown={startHold}
      onPointerUp={endHold}
      onPointerLeave={cancelHold}
      onPointerCancel={cancelHold}
    >
      {/* Feather graphic */}
      <div style={{ width: '54px', height: '108px' }}>
        <FeatherSVG type={feather.type} color={feather.color} featherId={feather.id}/>
      </div>

      {/* Handwritten label */}
      <div style={{
        marginTop: '9px',
        fontFamily: "'Caveat', cursive",
        fontSize: '11px',
        color: isPlaced ? 'rgba(255,215,0,0.18)' : 'rgba(255,215,0,0.52)',
        textAlign: 'center',
        lineHeight: '1.35',
        whiteSpace: 'pre-line',
        letterSpacing: '0.01em',
        userSelect: 'none',
        minHeight: '24px',
      }}>
        {feather.name}
      </div>
    </div>
  );
}
